import React, { Fragment, Ref, forwardRef } from 'react'

import clsx from 'clsx'
import { isEqual } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'

import { flip, shift, useFloating } from '@floating-ui/react'
import { Listbox, Transition } from '@headlessui/react'

import Icon from '@/components/Icon'

interface SelectOption {
  id: string
  title: string
  value: any
}

interface SelectProps {
  placeholder: string
  options: SelectOption[]
  disabled?: boolean
  className?: string
  buttonClassName?: string
  onChange?: (value: any) => void
  value?: any
  defaultValue?: any
  name?: string
}

const SortSelectBox = (
  {
    placeholder,
    options,
    disabled,
    className,
    buttonClassName,
    onChange,
    name,
    value,
    defaultValue
  }: SelectProps,
  ref: Ref<HTMLElement>
) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [flip(), shift({ crossAxis: true })]
  })

  const selectedOption = options.find(
    (option: SelectOption) =>
      JSON.stringify(option.value) == JSON.stringify(value)
  )
  const title = selectedOption?.title

  const onChangeHandler = (value: any) => {
    if (onChange) {
      onChange({ target: { value, name } })
    }
  }

  const { formatMessage } = useIntl()

  return (
    <div
      className={clsx(
        'select-none',
        disabled && 'pointer-events-none relative opacity-50',
        className
      )}
    >
      <Listbox
        className="relative"
        as="div"
        defaultValue={defaultValue}
        onChange={onChangeHandler}
        value={value}
      >
        <Listbox.Button
          className={clsx(
            'relative flex w-full cursor-pointer items-center justify-between',
            'whitespace-nowrap',
            'transition-all duration-150 ease-linear',
            buttonClassName
          )}
          ref={refs.setReference}
        >
          {({ open }) => (
            <>
              <span
                className={clsx(
                  'medium-text overflow-hidden whitespace-nowrap text-x-sm leading-x-sm',
                  'text-secondary-500 desktop:text-x-base desktop:leading-x-base',
                  title ? 'color-sub' : 'text-secondary-500'
                )}
                ref={ref}
              >
                {formatMessage({
                  id: selectedOption?.id,
                  defaultMessage: selectedOption?.title
                }) || placeholder}
              </span>
              <Icon
                className={clsx(
                  'ml-1 h-4 w-4 text-secondary-500 transition-transform duration-200',
                  open && '[transform:rotateZ(180deg)]'
                )}
                name="arrow-down-16"
              />
            </>
          )}
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="origin-top transition-[scale-y,opacity] duration-100 ease-in"
          enterFrom="scale-y-75 opacity-0"
          enterTo="scale-y-100 opacity-100"
          leave="origin-top transition-[scale-y,opacity] duration-100 ease-out"
          leaveFrom="scale-y-100 opacity-100"
          leaveTo="scale-y-75 opacity-0"
        >
          <Listbox.Options
            className={clsx(
              'scroll-wrap z-30 mt-2 max-h-60 w-max max-w-[80vw] overflow-auto',
              'rounded-md bg-white py-1 text-lg shadow-lg sm:text-sm'
            )}
            ref={refs.setFloating}
            style={floatingStyles}
          >
            {options?.map(({ title, value, id }, index) => (
              <Listbox.Option
                className="relative z-30 flex h-10 min-w-[280px] cursor-pointer select-none items-center px-3 py-2.5 hover:bg-gray-100"
                key={index}
                value={value}
              >
                {() => {
                  const selected = isEqual(value, selectedOption?.value)

                  return (
                    <span
                      className={clsx(
                        'flex w-full items-center justify-between overflow-hidden text-ellipsis whitespace-nowrap',
                        'text-x-sm leading-x-sm text-zink-800 desktop:text-x-base desktop:leading-x-base',
                        'transition-all duration-150 ease-linear',
                        selected ? 'font-medium' : 'font-normal'
                      )}
                    >
                      <FormattedMessage {...{ id, defaultMessage: title }} />
                      {selected && (
                        <Icon
                          className="ml-auto h-6 w-6 text-secondary-500"
                          name="check-24"
                        />
                      )}
                    </span>
                  )
                }}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </Listbox>
    </div>
  )
}

export default forwardRef(SortSelectBox)
